import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ChecklistItem, ManualChecklist } from "@tebiki/shared";
import { Tag, Btn } from "../components/index.js";
import { Icon } from "../components/Icon.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { useSurface } from "../lib/useSurface.js";
import { manuals } from "../lib/api.js";

/** Group checklist items by their step order number. */
function groupByStep(items: ChecklistItem[]): Map<number, ChecklistItem[]> {
  const map = new Map<number, ChecklistItem[]>();
  for (const item of items) {
    const bucket = map.get(item.order);
    if (bucket) {
      bucket.push(item);
    } else {
      map.set(item.order, [item]);
    }
  }
  return map;
}

/** Debounce a callback by `delay` ms. Returns a stable function that resets the timer on each call. */
function useDebounce<T extends (...args: Parameters<T>) => void>(fn: T, delay: number): T {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  return useCallback(
    ((...args: Parameters<T>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    }) as T,
    [delay],
  );
}

/**
 * Checklist screen — /checklist/:id
 *
 * Fetches the AI-generated checklist for a manual and lets any user (including viewers) tick
 * off items. Progress is persisted debounced to the server. Groups items by step order; renders
 * each as a large tappable checkbox row (≥ 56 px tap target). Shows "全項目完了" when all done.
 * Editors also see a "再生成" button to regenerate the checklist from current highlights.
 */
export function Checklist() {
  useSurface("field");
  const navigate = useNavigate();
  const { id = "" } = useParams();

  // --- fetch state ---
  const [checklist, setChecklist] = useState<ManualChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenNotice, setRegenNotice] = useState<string | null>(null);

  // --- progress state ---
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());

  // --- manual title (best-effort from cache) ---
  const [manualTitle, setManualTitle] = useState<string>("");

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setRegenNotice(null);
    try {
      const res = await manuals.getChecklist(id);
      setChecklist(res.checklist);
      setDoneIds(new Set(res.progress.doneItemIds));
    } catch (e) {
      setError((e as Error).message ?? "チェックリストの読み込みに失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Also fetch manual title (best-effort, ignored on failure).
  useEffect(() => {
    if (!id) return;
    manuals.get(id).then((m) => setManualTitle(m.title)).catch(() => {});
    void load();
  }, [id, load]);

  // --- persist progress (debounced, 800 ms) ---
  const persistProgress = useDebounce((ids: Set<string>) => {
    if (!id) return;
    manuals.saveChecklistProgress(id, Array.from(ids)).catch(() => {
      // silently retry on next toggle; no blocking UI error for background save
    });
  }, 800);

  function toggleItem(itemId: string) {
    setDoneIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      persistProgress(next);
      return next;
    });
  }

  async function regenerate() {
    if (!id) return;
    setRegenerating(true);
    setRegenNotice(null);
    try {
      const res = await manuals.regenerateChecklist(id);
      setChecklist(res.checklist);
      setDoneIds(new Set(res.progress.doneItemIds));
      setRegenNotice("チェックリストを再生成しました。");
    } catch {
      setRegenNotice("再生成に失敗しました。時間をおいて再試行してください。");
    } finally {
      setRegenerating(false);
    }
  }

  // --- derived values ---
  const items = checklist?.items ?? [];
  const total = items.length;
  const done = doneIds.size;
  const allDone = total > 0 && done >= total;
  const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;

  const grouped = useMemo(() => groupByStep(items), [items]);
  // Sorted step order numbers
  const stepOrders = useMemo(() => Array.from(grouped.keys()).sort((a, b) => a - b), [grouped]);

  // ---- Render ----
  return (
    <div className="shell">
      {/* ---- AppBar ---- */}
      <header className="appbar">
        <button
          className="btn btn--ghost"
          aria-label="戻る"
          onClick={() => navigate(-1)}
          style={{ minHeight: 56, minWidth: 56, padding: "8px 14px", borderRadius: 12 }}
        >
          <Icon name="chevron" size={20} />
        </button>
        <div className="grow">
          {manualTitle ? <div className="kicker">{manualTitle}</div> : null}
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 20, letterSpacing: "-0.01em" }}>
            <span className="hl">チェックリスト</span>
          </div>
        </div>
        {/* Regenerate button — always visible; server 403s gracefully for non-editors */}
        <button
          className="chip"
          onClick={regenerate}
          disabled={regenerating || loading}
          aria-label="チェックリストを再生成"
          style={{ minHeight: 48, opacity: regenerating ? 0.6 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {regenerating ? (
            "再生成中…"
          ) : (
            <>
              <Icon name="redo-cycle" size={14} />
              再生成
            </>
          )}
        </button>
      </header>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", padding: "0 0 120px" }}>
        {loading ? (
          <LoadingState label="チェックリストを読み込み中…" />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !checklist || items.length === 0 ? (
          <EmptyState icon={<Icon name="approval" size={30} />} title="チェックリストがまだありません">
            <p className="jp" style={{ margin: "8px 0 16px", color: "var(--muted)", fontSize: 15 }}>
              まず手順にマーカーで重要ポイントを付けるか、「再生成」でAIにチェックリストを作成させてください。
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <Btn variant="ghost" icon={<Icon name="edit" size={16} />} onClick={() => navigate(`/annotate/${id}`)}>
                マークを付ける
              </Btn>
              <Btn
                variant="hi"
                icon={regenerating ? undefined : <Icon name="redo-cycle" size={16} />}
                onClick={regenerate}
                disabled={regenerating}
              >
                {regenerating ? "生成中…" : "AIで生成"}
              </Btn>
            </div>
          </EmptyState>
        ) : (
          <>
            {/* ---- Progress bar + counter ---- */}
            <div style={{ padding: "24px 24px 0" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
                <span
                  style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(34px,7vw,56px)", lineHeight: 0.9, color: "transparent", WebkitTextStroke: "1.5px var(--hi)" }}
                  aria-hidden="true"
                >
                  {String(done).padStart(2, "0")}
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--muted)" }}>
                  / {total} 項目完了
                </span>
                <span style={{ flex: 1 }} />
                <Tag dot color={allDone ? "var(--ok)" : "var(--hi)"}>
                  {progressPct}%
                </Tag>
              </div>
              <div className="bar" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100} aria-label="チェックリストの進捗">
                <i style={{ width: `${progressPct}%` }} />
              </div>
            </div>

            {/* ---- Regeneration notice ---- */}
            {regenNotice ? (
              <div
                role="status"
                aria-live="polite"
                style={{ margin: "12px 24px 0", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--surface)", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--muted)" }}
              >
                {regenNotice}
              </div>
            ) : null}

            {/* ---- Celebratory state ---- */}
            {allDone ? (
              <div
                role="status"
                aria-live="polite"
                style={{
                  margin: "20px 24px",
                  padding: "22px 24px",
                  borderRadius: 18,
                  border: "2px solid var(--hi)",
                  background: "color-mix(in srgb, var(--hi) 10%, var(--surface))",
                  textAlign: "center",
                }}
              >
                <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(30px,7vw,56px)", fontWeight: 600, color: "var(--hi)" }} aria-hidden="true">
                  全項目完了
                </div>
                <p className="jp" style={{ margin: "8px 0 0", color: "var(--muted)", fontSize: 15 }}>
                  すべての確認項目をクリアしました。お疲れさまでした！
                </p>
              </div>
            ) : null}

            {/* ---- Grouped checklist items ---- */}
            <div style={{ padding: "16px 24px 0", display: "flex", flexDirection: "column", gap: 0 }}>
              {stepOrders.map((order) => {
                const stepItems = grouped.get(order)!;
                return (
                  <div key={order} style={{ marginBottom: 22 }}>
                    {/* Step group header */}
                    <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 4, paddingBottom: 8, borderBottom: "1px solid var(--line)" }}>
                      <span
                        style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(40px,8vw,72px)", lineHeight: 0.85, color: "transparent", WebkitTextStroke: "1.4px var(--line-2)", flexShrink: 0 }}
                        aria-hidden="true"
                      >
                        {String(order).padStart(2, "0")}
                      </span>
                      <span className="kicker" style={{ marginLeft: 4 }}>STEP {String(order).padStart(2, "0")}</span>
                    </div>

                    {/* Items for this step */}
                    <ul role="list" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                      {stepItems.map((item) => {
                        const checked = doneIds.has(item.id);
                        return (
                          <li key={item.id}>
                            <ChecklistRow
                              item={item}
                              checked={checked}
                              onToggle={() => toggleItem(item.id)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* ---- Sticky action bar ---- */}
      {!loading && !error && checklist && items.length > 0 ? (
        <div className="actionbar">
          <Btn variant="ghost" lg icon={<Icon name="chevron" size={18} />} onClick={() => navigate(`/viewer/${id}`)}>
            手順を見る
          </Btn>
          <span className="grow" />
          {allDone ? (
            <Btn variant="hi" lg icon={<Icon name="ai-review" size={18} />} onClick={() => navigate(`/digest/${id}`)}>
              要点ダイジェスト
            </Btn>
          ) : (
            <button
              onClick={() => {
                // Reset all items
                const next = new Set<string>();
                setDoneIds(next);
                persistProgress(next);
              }}
              style={{ minHeight: 56, padding: "0 22px", borderRadius: 14, border: "1px solid var(--line)", background: "var(--surface)", color: "var(--muted)", fontWeight: 600, cursor: "pointer" }}
              aria-label="チェックをリセット"
            >
              リセット
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** A single tappable checkbox row — ≥ 56 px tap target, keyboard + focus-visible, ARIA. */
function ChecklistRow({
  item,
  checked,
  onToggle,
}: {
  item: ChecklistItem;
  checked: boolean;
  onToggle: () => void;
}) {
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onToggle();
    }
  }

  return (
    <div
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={onKeyDown}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        minHeight: 56,
        padding: "10px 14px",
        borderRadius: 14,
        cursor: "pointer",
        background: checked ? "color-mix(in srgb, var(--hi) 8%, var(--surface))" : "var(--surface)",
        border: `1px solid ${checked ? "color-mix(in srgb, var(--hi) 40%, transparent)" : "var(--line)"}`,
        transition: "background .15s, border-color .15s",
        userSelect: "none",
      }}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
    >
      {/* Custom checkbox visual */}
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 26,
          height: 26,
          borderRadius: 8,
          border: checked ? "none" : "2px solid var(--line-2)",
          background: checked ? "var(--hi)" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          color: "#10130a",
          transition: "background .15s, border .15s",
        }}
      >
        {checked ? <Icon name="check" size={14} /> : null}
      </span>

      {/* Item text */}
      <span
        className="jp"
        style={{
          flex: 1,
          fontSize: 16,
          lineHeight: 1.55,
          color: checked ? "var(--muted)" : "var(--ink)",
          textDecoration: checked ? "line-through" : "none",
          transition: "color .15s",
        }}
      >
        {item.text}
      </span>
    </div>
  );
}
